import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Bug, Plus, AlertTriangle, Calendar, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PestRecords({ user }) {
  const [records, setRecords] = useState([]);
  const [plantings, setPlantings] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    crop_name: '', issue_type: 'pest', issue_name: '', severity: 'low',
    description: '', treatment: '', planting_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/pest-records`, { withCredentials: true }),
        axios.get(`${API}/plantings`, { withCredentials: true })
      ]);
      setRecords(r1.data);
      setPlantings(r2.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pest-records`, form, { withCredentials: true });
      setShowAdd(false);
      setForm({ crop_name: '', issue_type: 'pest', issue_name: '', severity: 'low', description: '', treatment: '', planting_id: '' });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const toggleResolved = async (recordId, current) => {
    try {
      await axios.patch(`${API}/pest-records/${recordId}`, { resolved: !current }, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Pest & Disease Log</h1>
            <p className="text-base text-[#5C665C] mt-2">Track issues to learn patterns and prevent next season</p>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-record-button" className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full flex items-center gap-2">
            <Plus className="w-5 h-5" />Log Issue
          </button>
        </div>

        {showAdd && (
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
            <h3 className="text-xl font-semibold text-[#1A4331] mb-4">Log Pest/Disease</h3>
            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-2">Crop / Planting</label>
                <select value={form.planting_id} onChange={e => {
                  const p = plantings.find(pl => pl.planting_id === e.target.value);
                  setForm({ ...form, planting_id: e.target.value, crop_name: p?.crop_name || '' });
                }} data-testid="planting-select" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none">
                  <option value="">Select crop</option>
                  {plantings.map(p => <option key={p.planting_id} value={p.planting_id}>{p.crop_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-2">Type</label>
                <select value={form.issue_type} onChange={e => setForm({ ...form, issue_type: e.target.value })} data-testid="issue-type-select" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none">
                  <option value="pest">Pest</option>
                  <option value="disease">Disease</option>
                  <option value="deficiency">Nutrient Deficiency</option>
                  <option value="physiological">Physiological (heat/water stress)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-2">Issue Name</label>
                <input type="text" value={form.issue_name} onChange={e => setForm({ ...form, issue_name: e.target.value })} placeholder="e.g., Aphids, Powdery mildew" data-testid="issue-name-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-2">Severity</label>
                <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} data-testid="severity-select" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#2D241E] mb-2">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Symptoms observed, when noticed..." data-testid="description-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#2D241E] mb-2">Treatment Applied</label>
                <textarea value={form.treatment} onChange={e => setForm({ ...form, treatment: e.target.value })} rows={2} placeholder="e.g., Sprayed with neem oil, removed affected leaves..." data-testid="treatment-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none resize-none" />
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" data-testid="save-record-button" className="bg-[#1A4331] text-white font-semibold py-3 px-6 rounded-full">Save Record</button>
                <button type="button" onClick={() => setShowAdd(false)} className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {records.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center">
            <Bug className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#2D241E] mb-2">No issues logged yet</h3>
            <p className="text-[#5C665C]">Track pests and diseases to learn patterns over time</p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.map(r => (
              <div key={r.record_id} data-testid={`record-${r.record_id}`} className="bg-white rounded-2xl p-5 border border-[#1A4331]/5 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    r.severity === 'high' ? 'bg-[#E2725B]/10' :
                    r.severity === 'medium' ? 'bg-[#D4AF37]/10' :
                    'bg-[#8EAA97]/10'
                  }`}>
                    <AlertTriangle className={`w-5 h-5 ${
                      r.severity === 'high' ? 'text-[#E2725B]' :
                      r.severity === 'medium' ? 'text-[#D4AF37]' :
                      'text-[#8EAA97]'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-semibold text-[#1A4331]">{r.issue_name} <span className="text-[#5C665C] font-normal text-sm">on {r.crop_name}</span></h3>
                      <button onClick={() => toggleResolved(r.record_id, r.resolved)} data-testid={`toggle-resolved-${r.record_id}`} className={`text-xs px-3 py-1 rounded-full font-semibold ${
                        r.resolved ? 'bg-[#B4D330]/20 text-[#1A4331]' : 'bg-[#E2725B]/10 text-[#E2725B]'
                      }`}>
                        {r.resolved ? '✓ Resolved' : 'Active'}
                      </button>
                    </div>
                    <p className="text-xs text-[#5C665C] mb-2 capitalize">{r.issue_type} · {r.severity} severity</p>
                    {r.description && <p className="text-sm text-[#2D241E] mb-2">{r.description}</p>}
                    {r.treatment && <p className="text-sm text-[#5C665C]"><strong>Treatment:</strong> {r.treatment}</p>}
                    <div className="flex items-center gap-2 mt-2 text-xs text-[#5C665C]">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(r.created_at).toLocaleDateString('en-NZ')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default PestRecords;
